import { useBackgroundQuery, useMutation, type QueryRef } from '@apollo/client/react';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { QueryOrderEnum, AppMembershipsOrderByEnum, AppMembershipRoleEnum } from 'src/graphql/schema';
import {
  AppMembershipsDocument,
  type IAppMembershipsQuery,
} from 'src/graphql/actions/appMemberships/appMemberships.query';
import { AppMembershipsCreateDocument } from 'src/graphql/actions/appMemberships/appMembershipsCreate.mutation';
import { createRequiredContext } from 'src/context/createRequiredContext';
import { ClientError, getApiError } from 'src/utils/errors';

import { useAppMembershipsManagementSlice, type IPendingMember } from './useAppMembershipsManagementSlice';

const [useAppMembershipsManagement, AppMembershipsManagementProvider] = createRequiredContext<{
  addSuccessCallback?: () => void;
  removeSuccessCallback?: () => void;
  queryAppMembershipsRef: QueryRef<IAppMembershipsQuery>;
  appId: string;
  canCreateMember: boolean;
  pendingMembers: IPendingMember[];
  addPendingMember: (member: Omit<IPendingMember, 'role'>) => void;
  removePendingMember: (id: string) => void;
  updatePendingMemberRole: (id: string, role: AppMembershipRoleEnum) => void;
  clearPendingMembers: () => void;
  saveAppMemberships: () => Promise<boolean>;
  isSaving: boolean;
}>();

type Props = {
  appId: string;
  canCreateMember: boolean;
  addSuccessCallback?: () => void;
  removeSuccessCallback?: () => void;
};

const AppMembershipsManagementContextProvider = ({
  children,
  appId,
  canCreateMember,
  addSuccessCallback,
  removeSuccessCallback,
}: React.PropsWithChildren<Props>) => {
  const [queryRef] = useBackgroundQuery(AppMembershipsDocument, {
    variables: {
      appId,
      page: 1,
      perPage: 100,
      orderBy: AppMembershipsOrderByEnum.Role,
      order: QueryOrderEnum.Asc,
    },
  });

  const queryAppMembershipsRef = useMemo(() => queryRef, [queryRef]);

  const { pendingMembers, addPendingMember, removePendingMember, updatePendingMemberRole, clearPendingMembers } =
    useAppMembershipsManagementSlice();

  const [createAppMemberships, { loading: isSaving }] = useMutation(AppMembershipsCreateDocument);

  const saveAppMemberships = async () => {
    try {
      const { data } = await createAppMemberships({
        variables: {
          input: {
            appId,
            appMembershipsParams: pendingMembers.map(({ id, role }) => ({
              userId: id,
              role,
            })),
          },
        },
      });

      if (!data?.appMembershipsCreate?.success) {
        throw new ClientError(data?.appMembershipsCreate?.errors?.[0]);
      }

      const count = pendingMembers.length;
      toast.success(`${count} member${count === 1 ? '' : 's'} added`);
      clearPendingMembers();
      addSuccessCallback?.();

      return true;
    } catch (error) {
      getApiError(error, toast.error);

      return false;
    }
  };

  const values = {
    appId,
    canCreateMember,
    addSuccessCallback,
    removeSuccessCallback,
    queryAppMembershipsRef,
    pendingMembers,
    addPendingMember,
    removePendingMember,
    updatePendingMemberRole,
    clearPendingMembers,
    saveAppMemberships,
    isSaving,
  };

  return <AppMembershipsManagementProvider value={values}>{children}</AppMembershipsManagementProvider>;
};

export { AppMembershipsManagementContextProvider, useAppMembershipsManagement };
